import { Controller } from '@hotwired/stimulus';

// Connects to data-controller="plan-selector"
export default class extends Controller {
  static targets = [
    'radio',
    'objectiveField',
    'option',
    'options',
    'pricePerDayLabel',
    'originalPricePerDayLabel',
  ];

  static values = {
    initialRecipe: String,
    initialIntervalCount: Number,
    initialObjective: String,
    initialPlanType: String,
    plans: Object,
    halfPlans: Object,
  };

  connect() {
    this.selectedPlansByRecipe = {};
    this.activePlanType = this.initialPlanTypeValue || 'full';

    if (this.hasPlansValue && Object.keys(this.plansValue || {}).length > 0) {
      const initialRecipe = this.initialRecipeValue;
      this.activeRecipe = initialRecipe;

      const serverRenderedHasRadios = this.radioTargets.length > 0;
      const startsOnHalf =
        this.activePlanType === 'half' &&
        this.hasHalfPlansForRecipe(initialRecipe);

      if (!serverRenderedHasRadios || startsOnHalf) {
        this.renderForRecipe(initialRecipe);
      } else {
        this.rememberSelectedPlan(initialRecipe);
        this.syncFromCheckedRadio();
      }

      this.updateHalfPlanVisibility();
      return;
    }

    // Fallback to static server-rendered behavior
    this.syncFromCheckedRadio();
    this.updateHalfPlanVisibility();
  }

  hasHalfPlansForRecipe(recipe) {
    return (
      this.hasHalfPlansValue &&
      Array.isArray(this.halfPlansValue?.[recipe]) &&
      this.halfPlansValue[recipe].length > 0
    );
  }

  plansSource(recipe = this.activeRecipe) {
    if (
      this.activePlanType === 'half' &&
      this.hasHalfPlansForRecipe(recipe)
    ) {
      return this.halfPlansValue;
    }
    return this.plansValue || {};
  }

  onPlanTypeChanged(event) {
    const planType = event.detail?.planType;
    if (!planType || planType === this.activePlanType) return;
    if (!this.hasPlansValue) return;

    this.rememberSelectedPlan(this.activeRecipe);
    this.activePlanType = planType;
    this.renderForRecipe(this.activeRecipe);
    this.updateHalfPlanVisibility();
  }

  updateHalfPlanVisibility() {
    if (!this.hasOptionsTarget) return;
    const isHalfPlan = this.activePlanType === 'half';

    this.optionsTarget.classList.toggle(
      'm-plan-option__group--readonly',
      isHalfPlan
    );

    this.radioTargets.forEach((radio) => {
      radio.disabled = isHalfPlan;
      if (isHalfPlan) {
        radio.setAttribute('aria-disabled', 'true');
      } else {
        radio.removeAttribute('aria-disabled');
      }
    });
  }

  selectPlan(event) {
    if (this.activePlanType === 'half') return;

    const selectedRadio = event.target;

    this.updateObjectiveField(selectedRadio);
    this.updateVisualState(selectedRadio);
    this.updatePricePerDayDisplay(selectedRadio);
    this.applyPlanToHiddenFields(selectedRadio);

    if (this.activeRecipe) {
      this.selectedPlansByRecipe[this.activeRecipe] = {
        intervalCount: parseInt(selectedRadio.value, 10),
        objective: selectedRadio.dataset.objective,
        optionKey: selectedRadio.dataset.optionKey,
      };
    }
  }

  syncFromCheckedRadio() {
    const checkedRadio = this.radioTargets.find((radio) => radio.checked);
    if (!checkedRadio) return;

    this.updateObjectiveField(checkedRadio);
    this.updateVisualState(checkedRadio);
    this.updatePricePerDayDisplay(checkedRadio);
    this.applyPlanToHiddenFields(checkedRadio);

    if (this.activeRecipe) {
      this.selectedPlansByRecipe[this.activeRecipe] = {
        intervalCount: parseInt(checkedRadio.value, 10),
        objective: checkedRadio.dataset.objective,
        optionKey: checkedRadio.dataset.optionKey,
      };
    }
  }

  onRecipeChanged(event) {
    const recipe = event.detail?.recipe;
    if (!recipe) return;

    if (!this.hasPlansValue) return;

    if (recipe === this.activeRecipe) return;

    this.rememberSelectedPlan(this.activeRecipe);
    this.activeRecipe = recipe;
    this.renderForRecipe(recipe);
  }

  updateObjectiveField(radio) {
    if (!this.hasObjectiveFieldTarget || !radio) return;

    this.objectiveFieldTarget.value = radio.dataset.objective;
  }

  updateVisualState(selectedRadio) {
    if (!selectedRadio) return;

    this.optionTargets.forEach((option) => {
      option.classList.remove('m-plan-option--selected');
    });

    const selectedIndex = this.radioTargets.indexOf(selectedRadio);
    if (selectedIndex === -1) return;

    const selectedOption = this.optionTargets[selectedIndex];
    if (selectedOption) {
      selectedOption.classList.add('m-plan-option--selected');
    }
  }

  updatePricePerDayDisplay(radio) {
    if (!radio) return;

    if (this.hasPricePerDayLabelTarget && radio.dataset.pricePerDayLabel) {
      this.pricePerDayLabelTarget.textContent = radio.dataset.pricePerDayLabel;
    }

    if (
      this.hasOriginalPricePerDayLabelTarget &&
      radio.dataset.originalPricePerDayLabel
    ) {
      this.originalPricePerDayLabelTarget.textContent =
        radio.dataset.originalPricePerDayLabel;
    }
  }

  rememberSelectedPlan(recipe) {
    if (!recipe) return;

    const checked = this.radioTargets.find((radio) => radio.checked);
    if (!checked) return;

    this.selectedPlansByRecipe[recipe] = {
      intervalCount: parseInt(checked.value, 10),
      objective: checked.dataset.objective,
      optionKey: checked.dataset.optionKey,
    };
  }

  renderForRecipe(recipe) {
    if (!this.hasOptionsTarget) return;

    const plans = this.plansSource(recipe)?.[recipe] || [];
    this.optionsTarget.innerHTML = '';

    if (plans.length === 0) {
      return;
    }

    // Sort plans to match server-side order:
    // 1. price_per_order objectives first
    // 2. Then by interval_count ascending
    const sortedPlans = this.sortPlans(plans);

    const desiredPlanIndex = this.resolveDesiredPlanIndex(recipe, sortedPlans);
    const isHalfPlan = this.activePlanType === 'half';

    sortedPlans.forEach((plan, index) => {
      const intervalCount = plan.interval_count;
      const objective = plan.objective;
      const pricePerDay = plan.price_per_day;
      const skuId = plan.sku_id;
      const quantity = plan.quantity;
      const optionKey = plan.option_key;

      const id = optionKey
        ? `plan_option_${recipe}_${optionKey}`
        : `plan_option_${recipe}_${index}`;
      const checked = index === desiredPlanIndex;

      const label = document.createElement('label');
      label.className = `m-plan-option${
        checked ? ' m-plan-option--selected' : ''
      }`;
      label.setAttribute('data-plan-selector-target', 'option');
      label.setAttribute('for', id);

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'subscription[interval_count]';
      input.value = intervalCount;
      input.id = id;
      input.className = 'm-plan-option__radio';
      input.checked = checked;
      input.disabled = isHalfPlan;
      if (isHalfPlan) input.setAttribute('aria-disabled', 'true');
      input.setAttribute('data-plan-selector-target', 'radio');
      input.setAttribute('data-action', 'change->plan-selector#selectPlan');
      input.dataset.objective = objective;
      if (optionKey) input.dataset.optionKey = optionKey;
      if (skuId) input.dataset.skuId = skuId;
      if (quantity) input.dataset.quantity = quantity;
      if (pricePerDay) {
        input.dataset.pricePerDayLabel = `$${Number(pricePerDay).toFixed(2)} per day`;
      }
      if (plan.feeding) input.dataset.feedingLabel = plan.feeding;

      const content = document.createElement('div');
      content.className = 'm-plan-option__content';

      content.innerHTML = `
        <div class="d-flex justify-content-between align-items-start">
          <div class="monument-regular stack stack--xs">
            <p class="m-0 mb-2 p-0 u-text-box-trim">
              <span class="monument-regular fw-bold c-text-sm">
                Every ${intervalCount} days
              </span>
            </p>
            ${
              pricePerDay
                ? `<p class="c-text-xs u-text-box-trim"><span class="monument-regular c-text-xs lh-1">$${Number(
                    pricePerDay
                  ).toFixed(2)} / day</span></p>`
                : ''
            }
          </div>
          <div class="c-text-xs mt-1">
            ${
              objective === 'price_per_order'
                ? ''
                : '<span class="m-label m-label--success-dark">Best Value</span>'
            }
          </div>
        </div>
      `;

      label.appendChild(input);
      label.appendChild(content);

      this.optionsTarget.appendChild(label);
    });

    const checkedRadio = this.optionsTarget.querySelector(
      'input[type="radio"]:checked'
    );
    if (checkedRadio) {
      this.updateObjectiveField(checkedRadio);
      this.applyPlanToHiddenFields(checkedRadio);
    }
  }

  sortPlans(plans) {
    return [...plans].sort((a, b) => {
      const aIsPricePerOrder = a.objective === 'price_per_order' ? 0 : 1;
      const bIsPricePerOrder = b.objective === 'price_per_order' ? 0 : 1;

      if (aIsPricePerOrder !== bIsPricePerOrder) {
        return aIsPricePerOrder - bIsPricePerOrder;
      }

      return a.interval_count - b.interval_count;
    });
  }

  resolveDesiredPlanIndex(recipe, plans) {
    const stored = this.selectedPlansByRecipe[recipe];
    if (stored) {
      const idx = plans.findIndex(
        (plan) =>
          stored.optionKey && plan.option_key === stored.optionKey
      );
      if (idx !== -1) return idx;

      const fallbackIdx = plans.findIndex(
        (plan) =>
          plan.interval_count === stored.intervalCount &&
          plan.objective === stored.objective
      );
      if (fallbackIdx !== -1) return fallbackIdx;

      const objectiveIdx = plans.findIndex(
        (plan) => plan.objective === stored.objective
      );
      if (objectiveIdx !== -1) return objectiveIdx;
    }

    const isInitialRecipe = recipe === this.initialRecipeValue;
    const initialObjective = this.hasInitialObjectiveValue
      ? this.initialObjectiveValue
      : null;

    if (isInitialRecipe) {
      const idx = plans.findIndex((plan) => {
        const intervalMatches =
          this.initialIntervalCountValue &&
          plan.interval_count === this.initialIntervalCountValue;
        const objectiveMatches = initialObjective
          ? plan.objective === initialObjective
          : true;

        return intervalMatches && objectiveMatches;
      });

      if (idx !== -1) return idx;

      const intervalIdx = plans.findIndex(
        (plan) =>
          this.initialIntervalCountValue &&
          plan.interval_count === this.initialIntervalCountValue
      );
      if (intervalIdx !== -1) return intervalIdx;
    }

    if (initialObjective) {
      const objectiveIdx = plans.findIndex(
        (plan) => plan.objective === initialObjective
      );
      if (objectiveIdx !== -1) return objectiveIdx;
    }

    return 0;
  }

  applyPlanToHiddenFields(radio) {
    if (!radio) return;

    const recipe = this.activeRecipe || this.initialRecipeValue;
    if (!recipe) return;

    const skuId = radio.dataset.skuId;
    const quantity = radio.dataset.quantity;

    const skuField = document.querySelector(`#sku_select_${recipe}`);
    if (skuField && skuId) {
      skuField.value = skuId;
    }

    const quantityField = document.querySelector(`#quantity_${recipe}`);
    if (quantityField && quantity) {
      quantityField.value = quantity;
    }
  }
}
